import type { DomainAutoRenew } from '../../../types/models/DomainName';

export type AutoRenewState = 'on' | 'off' | 'unknown';

/** Tri-state reading of the registrar flag (`enabled: null` = not synced / registrar without API). */
export function autoRenewState(autoRenew: DomainAutoRenew): AutoRenewState {
  if (autoRenew.enabled === null) return 'unknown';
  return autoRenew.enabled ? 'on' : 'off';
}

export const AUTO_RENEW_STATE_TEXT: Record<AutoRenewState, string> = {
  on: 'On',
  off: 'Off',
  unknown: 'Unknown',
};

/**
 * The renewal workflow's lapse, which the backend refuses to undo by hand — the same sentence as the
 * `PUT …/auto-renew` refusal, so the locked switch explains itself before anyone tries it.
 */
export const LAPSED_FOR_NONPAYMENT_TEXT =
  "This domain's renewal invoice is unpaid — pay it and auto-renew is switched back on automatically.";

/** A domain whose auto-renew the renewal workflow switched off (absent on an older backend = no). */
export function isLapsedForNonpayment(autoRenew: DomainAutoRenew): boolean {
  return autoRenew.lapsed_for_nonpayment === true;
}

export interface AutoRenewControl {
  state: AutoRenewState;
  /** An unknown flag renders unchecked (neutral); toggling it then turns auto-renew on. */
  isChecked: boolean;
  isDisabled: boolean;
  /** Visible explanation under the switch — why it is locked, or what the current state means. */
  hint: string | null;
  /** Follow the hint with a link to the invoices, where the unpaid renewal can be paid. */
  showInvoicesLink: boolean;
}

/**
 * How the detail-page auto-renew switch renders. Every permission comes from the backend block —
 * `supported` for any change, `can_disable` (already evaluated for the real user, so staff get the
 * override even while impersonating) for turning it off, `lapsed_for_nonpayment` for turning it back
 * on — and the backend's `reason` explains an unsupported one verbatim. `inTransfer` only picks the
 * fallback wording for a backend older than 0.86.1 (no `reason`); it never decides whether the
 * switch is usable.
 */
export function autoRenewControl(autoRenew: DomainAutoRenew, inTransfer: boolean): AutoRenewControl {
  const state = autoRenewState(autoRenew);
  const isChecked = state === 'on';

  if (!autoRenew.supported) {
    const fallback = inTransfer
      ? 'Auto-renew can be changed once the transfer completes.'
      : 'Auto-renew cannot be managed from the Manager for this domain.';
    return { state, isChecked, isDisabled: true, hint: autoRenew.reason ?? fallback, showInvoicesLink: false };
  }

  // Locked for everyone, staff included: paying the invoice is the only way back, and the workflow
  // then switches auto-renew on by itself.
  if (isLapsedForNonpayment(autoRenew)) {
    return { state, isChecked, isDisabled: true, hint: LAPSED_FOR_NONPAYMENT_TEXT, showInvoicesLink: true };
  }

  if (state === 'on' && !autoRenew.can_disable) {
    return {
      state,
      isChecked,
      isDisabled: true,
      hint: "This term's renewal has already been invoiced. Contact support to stop it.",
      showInvoicesLink: false,
    };
  }

  const hint: Record<AutoRenewState, string | null> = {
    // `can_disable` despite an invoiced renewal means staff: switching off leaves a credit note to issue.
    on: autoRenew.renewal_invoiced
      ? "This term's renewal has already been invoiced — turning auto-renew off requires a manual credit note."
      : null,
    off: 'The domain will not be renewed: it lapses at expiry and no renewal is invoiced.',
    unknown: 'Not yet confirmed with the registrar.',
  };

  return { state, isChecked, isDisabled: false, hint: hint[state], showInvoicesLink: false };
}
